import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { PackagePlus, Trash2, MoveRight, Boxes, Tag } from "lucide-react";

import classes from "../maintainers.module.scss";

import { filters } from "./products.constants";

import Head from "@/components/Content/Head";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import BasePagination from "@/components/Pagination/BasePagination";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import Fetching from "@/components/Fetching/Fetching";
import CodesMovements from "@/components/Product/ProductMovements/CodesMovements";
import ProductCriticalStock from "@/components/Product/ProductCriticalStock/ProductCriticalStock";
import IconSwitch from "@/components/desktop/list/filters/icon-switch/IconSwitch";
import CustomSelectActions from "@/components/desktop/list/select-actions/CustomSelectActions";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";
import ProductPending from "@/components/Product/ProductPending/ProductPending";

import ProductsFilter from "./ProductsFilter";
import ProductsForm from "./Form/ProductsForm";
import ProductsTableBody from "./ProductsTableBody";
import ProductsTableHead from "./ProductsTableHead";

import api from "@/services/api.service";

import { getToday, hasPermission, validRoles } from "@/services/Utils";
import { useProductsAvailable } from "@/hooks/products/useProducts";

import { FETCH_PRODUCT_SUCCESS, FINAL_PRODUCT_REQUEST, INITIAL_PRODUCT_REQUEST } from "@/redux/types/product.types";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";

import ProductsSelectCategory from "./ProductsSelectCategory";
import ProductBulk from "@/pages/Maintainers/Products/ProductBulk/ProductBulk.jsx";
import useLegacyProductPriceSync from "./hooks/useLegacyProductPriceSync";

const Products = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { section } = useParams();
	const filterCategory = searchParams.get("filterCategory");

	const { data: dataProducts } = useProductsAvailable(section);

	const [localeProducts, setLocaleProducts] = useState([]);
	useEffect(() => {
		if (dataProducts && dataProducts.products) {
			setLocaleProducts(dataProducts.products);
		}
	}, [dataProducts]);

	const { customer } = useSelector((state) => state.auth.user);
	const { search, estate_id, type_currency } = useSelector((state) => state.auth.filter);
	const { products, isFetching, quantity, last_page, from, to } = useSelector((state) => state.maintainers_products);

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isAdmin = validRoles(userRoles, ["administrador"]);
	const isMaintainerView = location.pathname.includes("mantenedores");
	const effectiveEstateId = isMaintainerView ? "" : estate_id;

	const inputRef = useRef(null);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(100);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);
	const [filter, setFilter] = useState({
		...filters,
		pathname: isMaintainerView ? "/mantenedores" : "/productos",
		customer_id: isMaintainerView ? "" : customer?.id,
		country_id: isMaintainerView ? "1" : customer?.country_id,
		is_generic: section === "genericos",
	});

	const [bulkSaveVersion, setBulkSaveVersion] = useState(0);

	const [showFilter, setShowFilter] = useState(false);
	const [showDelete, setShowDelete] = useState(false);
	const [showGroup, setShowGroup] = useState(false);
	const [showDeleteMultiple, setShowDeleteMultiple] = useState(false);
	const [showChange, setShowChange] = useState(false);
	const [groupProducts, setGroupProducts] = useState([]);
	const [groupTitles, setGroupTitles] = useState([]);
	const [permissions, setPermissions] = useState([]);

	useLegacyProductPriceSync({ products, dispatch });

	const updateData = useCallback(
		(response) => {
			if ("products" in response) {
				const {
					products,
					estates,
					countries,
					dealers,
					chemicals,
					aptitudes,
					categories,
					active_ingredients,
					base_permissions,
					crops,
					pests,
					titles,
					permissions,
				} = response;
				let localTitles = categories.map((category) => {
					category.link = `/mantenedores/productos?filterCategory=${category.label}`;
					return category;
				});
				localTitles.push({ label: "SIN CATEGORÍA", link: "/mantenedores/productos?filterCategory=SIN-CATEGORIA" });
				localTitles.push({ label: "TODOS", link: "/mantenedores/productos?filterCategory=TODOS" });

				if (!isMaintainerView) {
					localTitles = titles;
				}

				setGroupTitles(localTitles);
				setPermissions(permissions);

				dispatch({
					type: FETCH_PRODUCT_SUCCESS,
					payload: {
						products: products.data,
						last_page: products.last_page,
						from: products.from,
						to: products.to,
						quantity: products.total,
						estates: estates,
						countries: countries,
						dealers: dealers,
						chemicals: chemicals,
						aptitudes: aptitudes,
						categories: categories,
						active_ingredients: active_ingredients,
						base_permissions: base_permissions,
						crops: crops,
						pests: pests,
					},
				});
				setLoaded(true);
			} else {
				setLoaded(true);
			}
		},
		[dispatch, isMaintainerView],
	);

	const fetchProducts = useCallback(() => {
		dispatch({ type: INITIAL_PRODUCT_REQUEST });
		api.get("/admin/products", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id: effectiveEstateId,
			filterCategory,
			...filter,
		}).then((response) => {
			updateData(response);
		});
	}, [dispatch, page, paginate, order, orderType, search, effectiveEstateId, filterCategory, filter, updateData]);

	useEffect(() => {
		const handleBulkSaved = () => setBulkSaveVersion((version) => version + 1);
		globalThis.addEventListener("products-bulk-saved", handleBulkSaved);
		return () => {
			globalThis.removeEventListener("products-bulk-saved", handleBulkSaved);
		};
	}, []);

	useEffect(() => {
		fetchProducts();
	}, [fetchProducts, bulkSaveVersion]);

	const applyFilter = (params) => {
		const updatedFilter = { ...filter };
		let isFilterUpdated = false;

		const mappings = [
			{ paramKey: "filterCountry", filterKey: "country_id" },
			{ paramKey: "filterGeneric", filterKey: "is_generic" },
			{ paramKey: "filterCategory", filterKey: "category_id" },
			{ paramKey: "filterAptitud", filterKey: "aptitud_id" },
			{ paramKey: "filterUnit", filterKey: "unit_measurement" },
			{ paramKey: "filterChemical", filterKey: "chemical_id" },
			{ paramKey: "filterDealer", filterKey: "dealer_id" },
		];

		mappings.forEach(({ paramKey, filterKey }) => {
			if (params[paramKey] !== filter[filterKey]) {
				updatedFilter[filterKey] = params[paramKey];
				isFilterUpdated = true;
			}
		});

		if (isFilterUpdated) {
			setFilter(updatedFilter);
		}

		setShowFilter(false);
	};

	const handleDelete = (item) => {
		setSelected(item.id);
		setShowDelete(true);
	};

	const handlePriceClick = (product) => {
		if (product) {
			const params = new URLSearchParams(location.search);
			params.set("product", product.code);
			params.set("action", "price");
			params.delete("generic_estate_parent_id");
			navigate({ pathname: location.pathname, search: `?${params.toString()}` });
		}
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		const params = new URLSearchParams(location.search);
		const filterCategory = params.get("filterCategory");

		dispatch({ type: INITIAL_PRODUCT_REQUEST });
		api.post("/admin/products/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_id: effectiveEstateId,
			filterCategory,
			...filter,
		}).then((response) => {
			setSelected("");
			if ("products" in response) {
				toast.success("Producto eliminado correctamente.");
				updateData(response);
			} else {
				dispatch({ type: FINAL_PRODUCT_REQUEST });
			}
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		const params = new URLSearchParams(location.search);
		const filterCategory = params.get("filterCategory");

		api.post("/admin/products/save", {
			form: { ...form.form },
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id: effectiveEstateId,
			filterCategory,
			...filter,
		}).then((response) => {
			if ("products" in response) {
				updateData(response);
				modalFetching(false);
				afterSave();
			} else {
				modalFetching(false);
			}
		});
	};

	const onJoinProduct = (products, extraParams = {}) => {
		const params = new URLSearchParams(location.search);
		const filterCategory = params.get("filterCategory");

		dispatch({ type: INITIAL_PRODUCT_REQUEST });
		return api
			.post("/admin/products/join/", {
				page,
				selected,
				paginate,
				order,
				orderType,
				search,
				estate_id: effectiveEstateId,
				filterCategory,
				...filter,
				products,
				...extraParams,
			})
			.then((response) => {
				if ("products" in response) {
					updateData(response);
				} else {
					dispatch({ type: FINAL_PRODUCT_REQUEST });
				}
				return response;
			});
	};

	const handleGroup = () => {
		setShowGroup(true);
	};

	const confirmGroup = () => {
		const base_id = groupProducts[0].id;
		setShowGroup(false);

		onJoinProduct(groupProducts).then((response) => {
			setGroupProducts([]);
			if ("products" in response) {
				toast.success("Productos agrupados correctamente.");

				if (isMaintainerView) {
					navigate("/mantenedores/productos/editar/" + base_id);
				}
			}

			if (response?.response?.status === 400 || response?.isAxiosError) {
				const errorMessage = response?.response?.data?.data || "Ha ocurrido un error al juntar los productos";
				toast.error(errorMessage, { toastId: `create-join-error-${Date.now()}` });
			}
		});
	};

	const handleDeleteMultiple = () => {
		setShowDeleteMultiple(true);
	};

	const confirmDeleteMultiple = () => {
		setShowDeleteMultiple(false);

		const params = new URLSearchParams(location.search);
		const filterCategory = params.get("filterCategory");

		dispatch({ type: INITIAL_PRODUCT_REQUEST });
		api.post("/admin/products/delete_multiple", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_id: effectiveEstateId,
			filterCategory,
			...filter,
			products: groupProducts,
		}).then((response) => {
			setGroupProducts([]);
			if ("products" in response) {
				toast.success("Productos eliminados correctamente.");
				updateData(response);
			} else {
				dispatch({ type: FINAL_PRODUCT_REQUEST });
			}
		});
	};

	const handleChange = () => {
		setShowChange(true);
	};

	const confirmChange = (categoryValue) => {
		const params = new URLSearchParams(location.search);
		const filterCategory = params.get("filterCategory");

		dispatch({ type: INITIAL_PRODUCT_REQUEST });
		api.post("/admin/products/change_multiple", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_id: effectiveEstateId,
			filterCategory,
			...filter,
			products: groupProducts,
			selectCategoryValue: categoryValue,
		}).then((response) => {
			setGroupProducts([]);
			if ("products" in response) {
				toast.success("Productos cambiados correctamente.");
				updateData(response);
			} else {
				dispatch({ type: FINAL_PRODUCT_REQUEST });
			}
		});
	};

	const exportExcel = () => {
		const params = new URLSearchParams(location.search);
		const filterCategory = params.get("filterCategory");

		const filename = `Productos ${getToday()}.xlsx`;

		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: { page, paginate, order, orderType, search, estate_id: effectiveEstateId, filterCategory, ...filter },
					show: true,
					url: "/admin/products/export_excel",
					name: filename,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	const importExcel = () => {
		const file = inputRef.current?.files?.[0];
		if (!file) return;
		const formData = new FormData();
		formData.append("file", file);

		const params = new URLSearchParams(location.search);
		const filterCategory = params.get("filterCategory");

		dispatch({ type: INITIAL_PRODUCT_REQUEST });
		api.post("/admin/products/import_excel", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
			params: { page, paginate, order, orderType, search, estate_id: effectiveEstateId, filterCategory, ...filter },
		}).then((response) => {
			if ("products" in response) {
				toast.success("Productos importados correctamente.");
				updateData(response);
			} else {
				dispatch({ type: FINAL_PRODUCT_REQUEST });
			}
		});
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			{!isMaintainerView && (
				<>
					<Head
						titles={groupTitles}
						button={!filter.is_generic && hasPermission("create", permissions) ? true : false}
						button_text={"Agregar Producto"}
						action={() => navigate("/productos/etiquetados/agregar")}
						back_url={"/productos"}
					/>

					<ProductPending />
				</>
			)}

			{isMaintainerView && (
				<Head
					titles={groupTitles}
					button={true}
					button_text={"Nuevo Producto"}
					action={() => navigate("/mantenedores/productos/crear")}
					back_url={"/mantenedores"}
				/>
			)}

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />

			<Row className={` ${classes.main_row}`}>
				{showFilter && <ProductsFilter filter={filter} applyFilter={applyFilter} isMaintainerView={isMaintainerView} />}

				<Col md={showFilter ? 9 : 12} className={classes.main_col}>
					<Row className={`${classes.header} ${groupProducts.length > 0 && classes.have_action}`}>
						<ButtonFilter origin="actions" size_md={1} showFilter={showFilter} setShowFilter={setShowFilter} show_back={false} />

						{groupProducts.length > 0 ? (
							<Col md={7}>
								<CustomSelectActions
									iconMap={{ PackagePlus, Trash2, MoveRight }}
									actions={[
										{ label: "Juntar", icon: "PackagePlus", onClick: handleGroup },
										{ label: "Eliminar", icon: "Trash2", onClick: handleDeleteMultiple },
										isMaintainerView && { label: "Mover", icon: "MoveRight", onClick: handleChange },
									].filter(Boolean)}
								/>
							</Col>
						) : (
							<Col md={7}></Col>
						)}

						{isMaintainerView ? (
							<>
								<Col md={2} className={classes.colOptionButton}>
									<div className={`${classes.optionButton} ${classes.exportInventory}`} onClick={() => exportExcel()}>
										<span>Excel Productos</span>
									</div>
								</Col>
								<Col md={2} className={classes.colOptionButton}>
									<div
										className={`${classes.optionButton} ${classes.exportInventory} ${classes.up}`}
										onClick={() => inputRef.current.click()}
									>
										<span>Cargar Plantilla</span>
									</div>
									<input ref={inputRef} type="file" style={{ display: "none" }} onChange={() => importExcel()} />
								</Col>
							</>
						) : (
							<Col md={4} style={{ display: "flex", justifyContent: "flex-end" }}>
								<div className={`${classes.optionButtonNew}`} style={{ display: "none", marginRight: "15px" }} onClick={() => exportExcel()}>
									<span>Excel Productos</span>
								</div>

								<IconSwitch
									value={filter.is_generic}
									onChange={(value) => {
										setFilter((prevFilter) => ({
											...prevFilter,
											is_generic: value,
										}));

										navigate(value ? "/productos/genericos" : "/productos/etiquetados");
									}}
									options={[
										{ value: true, label: "Genéricos", icon: "Boxes" },
										{ value: false, label: "Etiquetados", icon: "Tag" },
									]}
									iconMap={{
										Boxes,
										Tag,
									}}
								/>
							</Col>
						)}
					</Row>

					{hasPermission("show", permissions) ? (
						<>
							<Row className={classes.table}>
								<div className={classes.table_div}>
									<Table className={classes.table_desktop}>
										<ProductsTableHead
											order={order}
											setOrder={setOrder}
											orderType={orderType}
											setOrderType={setOrderType}
											filter={filter}
											isMaintainerView={isMaintainerView}
										/>
										<ProductsTableBody
											handleDelete={hasPermission("delete", permissions) ? handleDelete : null}
											filter={filter}
											groupProducts={groupProducts}
											setGroupProducts={setGroupProducts}
											localeProducts={localeProducts}
											onJoinProduct={onJoinProduct}
											handlePriceClick={handlePriceClick}
											typeCurrency={type_currency}
										/>
									</Table>
								</div>
							</Row>

							<BasePagination
								page={page}
								paginate={paginate}
								last_page={last_page}
								quantity={quantity}
								from={from}
								to={to}
								setPage={setPage}
								setPaginate={setPaginate}
							/>
						</>
					) : (
						<CustomNoPermissionView />
					)}
				</Col>
			</Row>

			{loaded && (
				<>
					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />

					<BasicConfirm
						showConfirm={showGroup}
						setShowConfirm={setShowGroup}
						handleAction={confirmGroup}
						message={"¿Está seguro de que desea agrupar estos productos?"}
						subMessage={`Al realizar esta acción se abrirá un formulario para completar la información del producto unificado ${
							groupProducts.length > 0 ? groupProducts[0].tradename : ""
						}`}
						showValidation={true}
						textValidation={"juntar productos"}
					/>

					<BasicConfirm
						showConfirm={showDeleteMultiple}
						setShowConfirm={setShowDeleteMultiple}
						handleAction={confirmDeleteMultiple}
						message={"¿Está seguro de que deseas eliminar estos productos?"}
						showValidation={true}
						textValidation={"eliminar productos"}
					/>

					<ProductsSelectCategory showConfirm={showChange} setShowConfirm={setShowChange} confirmChange={confirmChange} />

					{isAdmin && <ProductsForm confirmSave={confirmSave} />}

					{!isMaintainerView && <ProductBulk />}

					<CodesMovements type={"Product"} />

					<ProductCriticalStock />
				</>
			)}
		</>
	);
};

export default Products;
